import React, { createContext, useContext, useState, useEffect } from 'react';
import { ACCENTS, DEFAULT_ACCENT } from './constants/accents';
import { coresDoEscritorio, VARIAVEIS } from './utils/corDoEscritorio';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  // Inicialização lendo do LocalStorage. Padrão é 'light': o tema claro é o principal
  // para todos no design EGORA (docs/redesign/referencias/design-system/README.md).
  const [theme, setTheme] = useState(() => localStorage.getItem('vm_theme') || 'light');
  const [accent, setAccent] = useState(() => {
    const stored = localStorage.getItem('vm_accent');
    return stored && ACCENTS[stored] ? stored : DEFAULT_ACCENT;
  });
  // A cor principal do escritório (Configurações → Identidade visual), lida da marca pela casca.
  const [corDoEscritorio, setCorDoEscritorio] = useState(null);

  // Efeito Global: Quando o tema mudar, injetamos a classe diretamente no <body>
  // Isso garante que nenhuma tela ou componente saia do esquadro.
  useEffect(() => {
    localStorage.setItem('vm_theme', theme);
    document.body.className = `theme-${theme}`;
  }, [theme]);

  // Efeito Global: Guardar a cor de destaque (Accent) corporativa.
  // Opcionalmente podemos injetar no body também, mas vamos manter no Context para os charts lerem.
  useEffect(() => {
    localStorage.setItem('vm_accent', accent);
    document.body.setAttribute('data-accent', accent);
  }, [accent]);

  // Design system EGORA: o tema vai em `data-theme` no html e no body, que é onde os
  // tokens novos leem. A classe do body (theme-*) continua para o CSS antigo.
  // A cor do escritório manda: entra nos tokens de destaque, com o tom ajustado ao
  // tema para manter a leitura. Sem ela, vale o destaque que a pessoa escolheu antes
  // (se escolheu) ou o marinho do design. O body recebe os valores também porque o
  // CSS antigo define as variáveis na classe do body, e um valor só no html seria escondido.
  useEffect(() => {
    const tema = theme === 'light' ? 'light' : 'dark';
    const doEscritorio = coresDoEscritorio(corDoEscritorio, tema === 'dark');
    const escolhida = localStorage.getItem('vm_accent_definido') === '1' && ACCENTS[accent];
    [document.documentElement, document.body].forEach((el) => {
      el.setAttribute('data-theme', tema);
      VARIAVEIS.forEach((v) => el.style.removeProperty(v));
      if (doEscritorio) VARIAVEIS.forEach((v) => el.style.setProperty(v, doEscritorio[v]));
      else if (escolhida) el.style.setProperty('--accent', ACCENTS[accent].brand);
    });
  }, [theme, accent, corDoEscritorio]);

  // Escolha feita pelo usuário ou pelo escritório: marca que o destaque foi definido.
  const escolherAcento = (nome) => {
    localStorage.setItem('vm_accent_definido', '1');
    setAccent(nome);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, accent, setAccent: escolherAcento, corDoEscritorio, setCorDoEscritorio }}>
      {children}
    </ThemeContext.Provider>
  );
};
